import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Ban, FileDown, RefreshCw, Search, Send, Truck } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Input, Select } from '@components/Input';
import { DateInputBR } from '@components/DateInputBR';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { invoiceShippingOrderService } from '@services/invoiceShippingOrderService';
import { shippingProviderService } from '@services/shippingProviderService';
import { paymentRoutingService } from '@services/paymentRoutingService';
import { Company } from '@/types/company';
import {
  InvoiceShippingOrderCandidate,
  InvoiceShippingOrderSearchModel,
  InvoiceShippingOrderSendResult,
} from '@/types/invoice';
import { ShippingProvider, ShippingProviderService } from '@/types/shippingProvider';
import { isCorreiosProviderName, isEligibleProviderName } from '@/utils/shippingProviders';
import type { CompanyCarrierBlock } from '@/types/paymentRouting';
import {
  BLOCKED_OPTION_BADGE,
  blockReason,
  isBlockedOption,
  isProviderFullyBlocked,
} from '@/utils/carrierBlocks';

interface ShippingOrderRow extends InvoiceShippingOrderCandidate {
  selected_provider_id: number | null;
  selected_service_id: number | null;
  available_services: ShippingProviderService[];
  services_loading: boolean;
  assignment_saving: boolean;
  send_loading: boolean;
  labels_loading: boolean;
  cancel_loading: boolean;
  row_error?: string | null;
}

const INTERNAL_PROVIDER_ID = 1;

const getToday = () => new Date().toISOString().split('T')[0];

const formatCurrency = (value?: number | null) =>
  value != null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (value: string) => {
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return date.toLocaleDateString('pt-BR');
};

// Identificação por nome compartilhada com a tela de Transportadoras (utils/shippingProviders).
const isEligibleProvider = (provider: ShippingProvider) => isEligibleProviderName(provider.provider_name);

const buildRow = (candidate: InvoiceShippingOrderCandidate): ShippingOrderRow => ({
  ...candidate,
  selected_provider_id:
    candidate.provider_id ?? candidate.quote_provider_id ?? candidate.integrated_provider_id ?? null,
  selected_service_id:
    candidate.provider_service_id ??
    candidate.quote_provider_service_id ??
    candidate.integrated_provider_service_id ??
    null,
  available_services: [],
  services_loading: false,
  assignment_saving: false,
  send_loading: false,
  labels_loading: false,
  cancel_loading: false,
  row_error: null,
});

const getEffectiveProviderId = (candidate: InvoiceShippingOrderCandidate): number | null =>
  candidate.provider_id ?? candidate.quote_provider_id ?? candidate.integrated_provider_id ?? null;

export const InvoiceShippingOrderListView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('invoice_shipping_orders.write').hasPermission;

  const [dateFilter, setDateFilter] = useState(getToday());
  const [search, setSearch] = useState('');
  const [companyIdFilter, setCompanyIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'integrated' | 'all'>('all');

  const [companies, setCompanies] = useState<Company[]>([]);
  const [providers, setProviders] = useState<ShippingProvider[]>([]);
  const [rows, setRows] = useState<ShippingOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  const serviceCacheRef = useRef<Record<number, ShippingProviderService[]>>({});
  // Bloqueios de transportadora/serviço por empresa emissora da NF (roteamento de pagamentos).
  // Só desabilitam as opções; o ERPApi é quem recusa (422) na seleção e no envio.
  const [blocksByCompany, setBlocksByCompany] = useState<Record<number, CompanyCarrierBlock[]>>({});
  const blocksRequestedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const missing = Array.from(new Set(rows.map((row) => row.company_id))).filter(
      (companyId) => companyId > 0 && !blocksRequestedRef.current.has(companyId),
    );
    missing.forEach((companyId) => {
      blocksRequestedRef.current.add(companyId);
      paymentRoutingService
        .getCarrierBlocks(companyId)
        .then((blocks) => setBlocksByCompany((current) => ({ ...current, [companyId]: blocks })))
        .catch(() => {
          // sem a lista a tela segue como antes; o backend continua recusando o par bloqueado
          blocksRequestedRef.current.delete(companyId);
        });
    });
  }, [rows]);

  const eligibleProviders = useMemo(
    () => providers.filter(isEligibleProvider),
    [providers],
  );

  const providerNameMap = useMemo(() => {
    const map = new Map<number, string>();
    eligibleProviders.forEach((provider) => map.set(provider.provider_id, provider.provider_name));
    return map;
  }, [eligibleProviders]);

  const updateRow = useCallback((invoiceId: number, updater: (row: ShippingOrderRow) => ShippingOrderRow) => {
    setRows((current) => current.map((row) => (row.invoice_id === invoiceId ? updater(row) : row)));
  }, []);

  const getServices = useCallback(async (providerId: number): Promise<ShippingProviderService[]> => {
    if (serviceCacheRef.current[providerId]) {
      return serviceCacheRef.current[providerId];
    }

    const services = await shippingProviderService.getProviderServicesByProvider(providerId);
    serviceCacheRef.current[providerId] = services;
    return services;
  }, []);

  const hydrateRowsWithServices = useCallback(
    async (items: ShippingOrderRow[]) => {
      const providerIds = Array.from(
        new Set(items.map((item) => item.selected_provider_id).filter((value): value is number => value != null)),
      );

      await Promise.all(providerIds.map((providerId) => getServices(providerId)));

      return items.map((item) => ({
        ...item,
        available_services:
          item.selected_provider_id != null
            ? serviceCacheRef.current[item.selected_provider_id] ?? []
            : [],
      }));
    },
    [getServices],
  );

  const loadFiltersData = useCallback(async () => {
    try {
      const [companyItems, providerItems] = await Promise.all([
        companyService.getCompanies(),
        shippingProviderService.getProviders(),
      ]);
      setCompanies(companyItems);
      setProviders(providerItems.filter(isEligibleProvider));
    } catch {
      // filtros auxiliares; falha não bloqueia a tela principal
    }
  }, []);

  const loadRows = useCallback(async () => {
    const query: InvoiceShippingOrderSearchModel = {
      date: dateFilter,
      page: currentPage,
      pageSize,
      search: search.trim() || undefined,
      company_id: companyIdFilter ? Number(companyIdFilter) : undefined,
      integration_status: statusFilter,
    };

    try {
      setLoading(true);
      setError(null);
      const result = await invoiceShippingOrderService.getInvoices(query);
      // Hide invoices whose effective provider is Viver Organizer (ProviderId = 1).
      // Also hide invoices with rejected status (StatusId = 4).
      const REJECTED_STATUS = 4;
      const visibleCandidates = result.data.filter(
        (candidate) =>
          getEffectiveProviderId(candidate) !== INTERNAL_PROVIDER_ID &&
          candidate.invoice_status !== REJECTED_STATUS,
      );
      const mapped = await hydrateRowsWithServices(visibleCandidates.map(buildRow));
      setRows(mapped);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar pedidos de envio');
    } finally {
      setLoading(false);
    }
  }, [companyIdFilter, currentPage, dateFilter, hydrateRowsWithServices, search, statusFilter]);

  useEffect(() => {
    loadFiltersData();
  }, [loadFiltersData]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadRows();
  };

  const persistAssignment = useCallback(
    async (invoiceId: number, providerId: number | null, serviceId: number | null) => {
      updateRow(invoiceId, (row) => ({ ...row, assignment_saving: true, row_error: null }));
      try {
        const updated = await invoiceShippingOrderService.updateAssignment(invoiceId, {
          provider_id: providerId,
          provider_service_id: serviceId,
        });
        updateRow(invoiceId, (row) => ({
          ...row,
          ...updated,
          selected_provider_id: providerId,
          selected_service_id: serviceId,
          available_services:
            providerId != null ? serviceCacheRef.current[providerId] ?? row.available_services : [],
          assignment_saving: false,
          row_error: null,
        }));
      } catch (err) {
        updateRow(invoiceId, (row) => ({
          ...row,
          assignment_saving: false,
          row_error:
            err instanceof Error ? err.message : 'Erro ao salvar seleção de transportadora',
        }));
      }
    },
    [updateRow],
  );

  const handleProviderChange = async (invoiceId: number, value: string) => {
    const providerId = value ? Number(value) : null;
    updateRow(invoiceId, (row) => ({
      ...row,
      selected_provider_id: providerId,
      selected_service_id: null,
      available_services: [],
      services_loading: providerId != null,
      row_error: null,
    }));

    if (providerId == null) {
      await persistAssignment(invoiceId, null, null);
      return;
    }

    try {
      const services = await getServices(providerId);
      const autoSelectedServiceId = services.length === 1 ? services[0].provider_service_id : null;
      updateRow(invoiceId, (row) => ({
        ...row,
        available_services: services,
        services_loading: false,
        selected_service_id: autoSelectedServiceId,
      }));
      await persistAssignment(invoiceId, providerId, autoSelectedServiceId);
    } catch (err) {
      updateRow(invoiceId, (row) => ({
        ...row,
        services_loading: false,
        row_error: err instanceof Error ? err.message : 'Erro ao carregar serviços da transportadora',
      }));
    }
  };

  const handleServiceChange = async (invoiceId: number, value: string) => {
    const serviceId = value ? Number(value) : null;
    const row = rows.find((item) => item.invoice_id === invoiceId);
    if (!row) return;

    updateRow(invoiceId, (current) => ({
      ...current,
      selected_service_id: serviceId,
      row_error: null,
    }));
    await persistAssignment(invoiceId, row.selected_provider_id, serviceId);
  };

  const applySendResult = useCallback(
    (invoiceId: number, result: InvoiceShippingOrderSendResult) => {
      updateRow(invoiceId, (row) => ({
        ...row,
        is_integrated: true,
        shipping_order_id: result.shipping_order_id ?? row.shipping_order_id,
        provider_id: result.provider_id,
        provider_service_id: result.provider_service_id ?? row.selected_service_id,
        selected_provider_id: result.provider_id,
        selected_service_id: result.provider_service_id ?? row.selected_service_id,
        provider_name:
          result.provider_name ?? providerNameMap.get(result.provider_id) ?? row.provider_name,
        provider_service_name:
          result.provider_service_name ??
          row.available_services.find(
            (service) => service.provider_service_id === (result.provider_service_id ?? row.selected_service_id),
          )?.provider_service_name ??
          row.provider_service_name,
        send_loading: false,
        row_error: null,
      }));
    },
    [providerNameMap, updateRow],
  );

  const handleSend = async (row: ShippingOrderRow) => {
    if (row.selected_provider_id == null || row.selected_service_id == null) {
      updateRow(row.invoice_id, (current) => ({
        ...current,
        row_error: 'Selecione uma transportadora e um serviço antes de enviar',
      }));
      return;
    }

    updateRow(row.invoice_id, (current) => ({ ...current, send_loading: true, row_error: null }));
    try {
      const result = await invoiceShippingOrderService.send(row.invoice_id, {
        provider_id: row.selected_provider_id,
        provider_service_id: row.selected_service_id,
      });
      applySendResult(row.invoice_id, result);
    } catch (err) {
      updateRow(row.invoice_id, (current) => ({
        ...current,
        send_loading: false,
        row_error: err instanceof Error ? err.message : 'Erro ao enviar nota fiscal',
      }));
    }
  };

  const handleDownloadCorreiosLabels = async (row: ShippingOrderRow) => {
    updateRow(row.invoice_id, (current) => ({ ...current, labels_loading: true, row_error: null }));
    try {
      await invoiceShippingOrderService.downloadCorreiosLabels(row.invoice_id);
      updateRow(row.invoice_id, (current) => ({ ...current, labels_loading: false }));
    } catch (err) {
      updateRow(row.invoice_id, (current) => ({
        ...current,
        labels_loading: false,
        row_error: err instanceof Error ? err.message : 'Erro ao baixar etiquetas dos Correios',
      }));
    }
  };

  const handleCancelCorreios = async (row: ShippingOrderRow) => {
    const confirmed = window.confirm(
      `Cancelar as pré-postagens dos Correios da NF ${row.number}/${row.serie}? A nota voltará a ficar pendente de integração.`,
    );
    if (!confirmed) return;

    updateRow(row.invoice_id, (current) => ({ ...current, cancel_loading: true, row_error: null }));
    try {
      await invoiceShippingOrderService.cancelShippingOrder(row.invoice_id);
      updateRow(row.invoice_id, (current) => ({
        ...current,
        cancel_loading: false,
        is_integrated: false,
        shipping_order_id: null,
        integrated_provider_id: null,
        integrated_provider_service_id: null,
        row_error: null,
      }));
    } catch (err) {
      updateRow(row.invoice_id, (current) => ({
        ...current,
        cancel_loading: false,
        row_error: err instanceof Error ? err.message : 'Erro ao cancelar o pedido de envio nos Correios',
      }));
    }
  };

  return (
    <PermissionGuard
      permission="invoice_shipping_orders.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para acessar Pedidos de Envio.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-h1">Pedidos de Envio</h1>
            <p className="text-slate-600">
              Selecione a transportadora e o serviço de frete das notas fiscais de saída do dia.
            </p>
          </div>
          <Button variant="outline" onClick={loadRows} icon={<RefreshCw size={18} />}>
            Atualizar
          </Button>
        </div>

        <Card>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)_220px_220px]">
              <div>
                <label className="text-label block mb-1">Data</label>
                <DateInputBR
                  className="input-field px-3 py-2 text-sm w-full"
                  value={dateFilter}
                  onChange={(v) => setDateFilter(v)}
                />
              </div>
              <Input
                label="Buscar"
                placeholder="Nota, cliente ou protocolo"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                startIcon={<Search size={18} />}
              />
              <Select
                label="Empresa"
                value={companyIdFilter}
                onChange={(e) => setCompanyIdFilter(e.target.value)}
              >
                <option value="">Todas</option>
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>
                    {company.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Situação"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'pending' | 'integrated' | 'all')}
              >
                <option value="all">Todas</option>
                <option value="pending">Pendentes</option>
                <option value="integrated">Integradas</option>
              </Select>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSearch} icon={<Search size={18} />}>
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader
            title="Notas fiscais prontas para integração"
            subtitle="A lista sempre considera somente notas fiscais de saída (Type = 'S')."
            action={<span className="text-sm text-slate-500">{totalItems} registro(s)</span>}
          />
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <RefreshCw size={22} className="animate-spin" />
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Truck size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nenhuma nota fiscal encontrada para a data selecionada.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Nota</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Empresa</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Transportadora</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Serviço</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Situação</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Protocolo</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.invoice_id} className="border-b border-slate-200 align-top hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-800">NF {row.number}/{row.serie}</p>
                          <p className="text-xs text-slate-500">InvoiceId #{row.invoice_id}</p>
                          <p className="text-xs text-slate-500">{formatDate(row.generation_timestamp)}</p>
                          <p className="text-xs text-slate-500">{formatCurrency(row.total_amount)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-700">{row.customer_name || '—'}</p>
                          <p className="text-xs text-slate-500">{row.customer_document || 'Documento não informado'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-700">{row.company_name || '—'}</p>
                          <p className="text-xs text-slate-500">Empresa #{row.company_id}</p>
                        </td>
                        <td className="px-4 py-3 min-w-[220px]">
                          <Select
                            value={row.selected_provider_id ?? ''}
                            disabled={!canWrite || row.is_integrated || row.assignment_saving}
                            onChange={(e) => handleProviderChange(row.invoice_id, e.target.value)}
                          >
                            <option value="">Selecione</option>
                            {eligibleProviders.map((provider) => {
                              const fullyBlocked = isProviderFullyBlocked(blocksByCompany[row.company_id], provider.provider_id);
                              return (
                                <option
                                  key={provider.provider_id}
                                  value={provider.provider_id}
                                  disabled={fullyBlocked && provider.provider_id !== row.selected_provider_id}
                                >
                                  {provider.provider_name}{fullyBlocked ? ` — ${BLOCKED_OPTION_BADGE.toLowerCase()}` : ''}
                                </option>
                              );
                            })}
                          </Select>
                          {row.has_order_quote_selection && (
                            <p className="mt-1 text-xs text-blue-600">Pré-preenchido pela cotação selecionada do pedido.</p>
                          )}
                        </td>
                        <td className="px-4 py-3 min-w-[220px]">
                          <Select
                            value={row.selected_service_id ?? ''}
                            disabled={
                              !canWrite ||
                              row.is_integrated ||
                              row.selected_provider_id == null ||
                              row.services_loading ||
                              row.assignment_saving
                            }
                            onChange={(e) => handleServiceChange(row.invoice_id, e.target.value)}
                          >
                            <option value="">{row.services_loading ? 'Carregando...' : 'Selecione'}</option>
                            {row.available_services.map((service) => {
                              const serviceBlocked = isBlockedOption(
                                blocksByCompany[row.company_id],
                                row.selected_provider_id,
                                service.provider_service_id,
                              ) != null;
                              return (
                                <option
                                  key={service.provider_service_id}
                                  value={service.provider_service_id}
                                  disabled={serviceBlocked && service.provider_service_id !== row.selected_service_id}
                                >
                                  {service.provider_service_name}{serviceBlocked ? ` — ${BLOCKED_OPTION_BADGE.toLowerCase()}` : ''}
                                </option>
                              );
                            })}
                          </Select>
                          {row.selected_provider_id != null && row.available_services.length === 1 && (
                            <p className="mt-1 text-xs text-emerald-600">Serviço único selecionado automaticamente.</p>
                          )}
                          {(() => {
                            const block = row.is_integrated
                              ? null
                              : isBlockedOption(
                                blocksByCompany[row.company_id],
                                row.selected_provider_id,
                                row.selected_service_id,
                              );
                            return block ? (
                              <span
                                className="mt-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                                title={blockReason(block)}
                              >
                                {BLOCKED_OPTION_BADGE}
                              </span>
                            ) : null;
                          })()}
                          {row.row_error && (
                            <p className="mt-1 text-xs text-red-600">{row.row_error}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.is_integrated ? (
                            <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                              Integrada
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                              Pendente
                            </span>
                          )}
                          {row.assignment_saving && (
                            <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                              <RefreshCw size={12} className="animate-spin" /> Salvando seleção...
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.shipping_order_id ? (
                            <span className="font-mono text-xs text-slate-700">{row.shipping_order_id}</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.is_integrated &&
                          isCorreiosProviderName(
                            row.provider_name ??
                              providerNameMap.get(
                                row.integrated_provider_id ?? row.selected_provider_id ?? -1,
                              ),
                          ) ? (
                            <div className="flex flex-col items-end gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                loading={row.labels_loading}
                                onClick={() => handleDownloadCorreiosLabels(row)}
                                icon={<FileDown size={14} />}
                              >
                                Etiquetas Correios
                              </Button>
                              {canWrite && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  loading={row.cancel_loading}
                                  onClick={() => handleCancelCorreios(row)}
                                  icon={<Ban size={14} />}
                                >
                                  Cancelar pré-postagem
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              disabled={
                                !canWrite ||
                                row.is_integrated ||
                                row.selected_provider_id == null ||
                                row.selected_service_id == null ||
                                row.assignment_saving ||
                                isBlockedOption(
                                  blocksByCompany[row.company_id],
                                  row.selected_provider_id,
                                  row.selected_service_id,
                                ) != null
                              }
                              loading={row.send_loading}
                              onClick={() => handleSend(row)}
                              icon={<Send size={14} />}
                            >
                              Enviar
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
                <p className="text-sm text-slate-600">Página {currentPage} de {totalPages}</p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage((page) => page - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage((page) => page + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGuard>
  );
};